import React from 'react';
import dayjs from 'dayjs';
import { PrintFooter, printStyles } from './PrintBase';

const formatCurrency = (val) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val || 0);

const STATUS_LABELS = {
  creada: 'Pendiente de Revisión',
  revisada: 'Pendiente de Confirmación',
  confirmada: 'Confirmada'
};

const FacturaPrint = React.forwardRef(function FacturaPrint({ factura }, ref) {
  if (!factura) return <div ref={ref} />;

  const detalles = factura.detalles || [];

  return (
    <div ref={ref} style={printStyles.page}>
      <div style={printStyles.header}>
        <div>
          <img src="/images/logoRadtrans.png" alt="RADTRANS" style={{ height: '32pt' }} />
          <p style={printStyles.logoSub}>Sistema de Gestión de Inventario</p>
        </div>
        <div style={printStyles.dateBlock}>
          <p style={{ ...printStyles.dateLabel, fontSize: '9pt' }}>FACTURA — CONSUMO INTERNO</p>
          <p style={{ ...printStyles.dateValue, fontSize: '16pt', color: '#1677ff' }}>
            {factura.numero_factura}
          </p>
        </div>
      </div>

      <h2 style={printStyles.title}>Validación de Factura de Consumo Interno</h2>
      <p style={printStyles.subtitle}>
        Estado: <strong>{STATUS_LABELS[factura.status] || factura.status}</strong>
      </p>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12pt' }}>
        <tbody>
          <tr>
            <td style={{ ...printStyles.td, width: '50%', verticalAlign: 'top' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4pt', fontSize: '9.5pt' }}>PROVEEDOR / EMISOR</div>
              <div style={{ fontSize: '9pt' }}>
                <div><strong>{factura.proveedor_nombre}</strong></div>
                <div>RFC: {factura.rfc_proveedor || '—'}</div>
                <div>Fecha de factura: {factura.fecha_factura ? dayjs(factura.fecha_factura).format('DD/MM/YYYY') : '—'}</div>
                <div>Concepto: {factura.concepto || '—'}</div>
              </div>
            </td>
            <td style={{ ...printStyles.td, width: '50%', verticalAlign: 'top' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4pt', fontSize: '9.5pt' }}>SEGUIMIENTO</div>
              <div style={{ fontSize: '9pt' }}>
                <div>Creada por: {factura.creador?.nombre || '—'}</div>
                {factura.revisor?.nombre && (
                  <div>Revisada por: {factura.revisor.nombre}
                    {factura.fecha_revision && ` (${dayjs(factura.fecha_revision).format('DD/MM/YYYY HH:mm')})`}
                  </div>
                )}
                {factura.confirmador?.nombre && (
                  <div>Confirmada por: {factura.confirmador.nombre}
                    {factura.fecha_confirmacion && ` (${dayjs(factura.fecha_confirmacion).format('DD/MM/YYYY HH:mm')})`}
                  </div>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div style={printStyles.sectionTitle}>Detalle de Productos</div>
      <table style={printStyles.table}>
        <thead>
          <tr>
            <th style={printStyles.th}>Descripción</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Cantidad</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Costo Unit.</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>IVA %</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Total</th>
            <th style={{ ...printStyles.th, textAlign: 'center' }}>¿Corregido?</th>
          </tr>
        </thead>
        <tbody>
          {detalles.map((d) => (
            <tr key={d.id}>
              <td style={printStyles.td}>
                {d.fue_corregido ? (
                  <>
                    <div style={{ color: '#888', textDecoration: 'line-through', fontSize: '8pt' }}>{d.descripcion_original}</div>
                    <div style={{ color: '#1677ff', fontWeight: 'bold' }}>{d.descripcion_efectiva}</div>
                  </>
                ) : d.descripcion_original}
              </td>
              <td style={printStyles.tdRight}>
                {d.fue_corregido && parseFloat(d.cantidad_original) !== d.cantidad_efectiva ? (
                  <>
                    <div style={{ color: '#888', textDecoration: 'line-through', fontSize: '8pt' }}>{parseFloat(d.cantidad_original)}</div>
                    <div style={{ color: '#1677ff', fontWeight: 'bold' }}>{d.cantidad_efectiva}</div>
                  </>
                ) : parseFloat(d.cantidad_original)}
              </td>
              <td style={printStyles.tdRight}>
                {d.fue_corregido && parseFloat(d.costo_unitario_original) !== d.costo_unitario_efectivo ? (
                  <>
                    <div style={{ color: '#888', textDecoration: 'line-through', fontSize: '8pt' }}>{formatCurrency(d.costo_unitario_original)}</div>
                    <div style={{ color: '#1677ff', fontWeight: 'bold' }}>{formatCurrency(d.costo_unitario_efectivo)}</div>
                  </>
                ) : formatCurrency(d.costo_unitario_original)}
              </td>
              <td style={printStyles.tdRight}>{d.iva_porcentaje_efectivo}%</td>
              <td style={{ ...printStyles.tdRight, fontWeight: 'bold' }}>{formatCurrency(d.total_efectivo)}</td>
              <td style={{ ...printStyles.tdCenter }}>
                {d.fue_corregido ? (
                  <span style={{ color: d.usar_original ? '#888' : '#1677ff', fontWeight: 'bold' }}>
                    {d.usar_original ? 'Revertido' : 'Sí'}
                  </span>
                ) : 'No'}
              </td>
            </tr>
          ))}
          {detalles.length === 0 && (
            <tr>
              <td colSpan={6} style={{ ...printStyles.tdCenter, color: '#888', fontStyle: 'italic' }}>Sin productos</td>
            </tr>
          )}
        </tbody>
        {detalles.length > 0 && (
          <tfoot>
            <tr style={printStyles.trSummary}>
              <td colSpan={4} style={{ ...printStyles.td, fontWeight: 'bold' }}>TOTALES</td>
              <td style={{ ...printStyles.tdRight, fontWeight: 'bold', fontSize: '10pt' }}>{formatCurrency(factura.total)}</td>
              <td style={printStyles.td} />
            </tr>
          </tfoot>
        )}
      </table>

      {factura.notas && (
        <>
          <div style={printStyles.sectionTitle}>Notas</div>
          <p style={{ fontSize: '9pt' }}>{factura.notas}</p>
        </>
      )}

      <div style={{ display: 'flex', gap: '24pt', marginTop: '30pt' }}>
        <div style={{ flex: 1, textAlign: 'center', borderTop: '1pt solid #333', paddingTop: '4pt', fontSize: '8.5pt' }}>
          Creó
        </div>
        <div style={{ flex: 1, textAlign: 'center', borderTop: '1pt solid #333', paddingTop: '4pt', fontSize: '8.5pt' }}>
          Revisó
        </div>
        <div style={{ flex: 1, textAlign: 'center', borderTop: '1pt solid #333', paddingTop: '4pt', fontSize: '8.5pt' }}>
          Confirmó (Gerencia)
        </div>
      </div>

      <PrintFooter />
    </div>
  );
});

export default FacturaPrint;
